import React, { useContext, useEffect, useState } from "react";
import IDiscount from "../../../models/IDiscount";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { Socket } from "socket.io-client";
import Typeahead from "../Typeahead/Typeahead";
import IGetDiscountsDTO from "../../../models/DTO/IGetDiscountsDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IOnGetDiscountsDTO from "../../../models/DTO/IOnGetDiscountsDTO";
import PriceUpchargeTypeEnum from "../../../models/PriceUpchargeTypeEnum";
import { OwIconButton } from "../OwIconButton/OwIconButton";
import OwLabel from "../OwLabel/OwLabel";

type Props = {
    socket: Socket;
    setDiscounts: (discounts: IDiscount[]) => void;
    selectedDiscounts: IDiscount[];
    maxAllowed?: number;
};

export const DiscountAssignment = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [typeaheadOptions, setTypeheadOptions] = useState<IDiscount[]>([]);
    const [discounts, setDiscounts] = useState<IDiscount[]>(props.selectedDiscounts || []);
    const [selectedDiscount, setSelectedDiscount] = useState<IDiscount | null>(null);
    const [clearTypeahead, setClearTypeahead] = useState(false);

    useEffect(() => {
        setDiscounts(props.selectedDiscounts || []);
    }, [props.selectedDiscounts]);

    useEffect(() => {
        if (selectedDiscount) {
            addSelectedDiscount(selectedDiscount);
        }
    }, [selectedDiscount]);

    const isSelectable = (): boolean => {
        return true;
    };

    const getDiscounts = (name: string) => {
        const payload: IGetDiscountsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            name,
        };

        props.socket.emit(EndpointsEnum.DiscountsGetDiscounts, payload);
    };

    const onGetDiscounts = (data: IOnGetDiscountsDTO) => {
        setTypeheadOptions(data.discounts);
    };

    const clearTypeaheadFn = () => {
        setSelectedDiscount(null);
        setTypeheadOptions([]);
    };

    const addSelectedDiscount = (discount: IDiscount) => {
        if (discount) {
            const updatedDiscounts = [...discounts];

            let toAdd = true;

            for (const existingDiscount of updatedDiscounts) {
                if (existingDiscount.id === discount.id) {
                    toAdd = false;
                }
            }

            if (toAdd) {
                updatedDiscounts.push(discount);

                setDiscounts(updatedDiscounts);
                props.setDiscounts(updatedDiscounts);
            }
        }
    };

    const removeDiscount = (discount: IDiscount) => {
        const existingDiscounts = [...discounts];

        for (let i = 0; i < existingDiscounts.length; ++i) {
            if (existingDiscounts[i].id === discount.id) {
                existingDiscounts.splice(i, 1);
            }
        }

        setDiscounts(existingDiscounts);
        props.setDiscounts(existingDiscounts);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.DiscountsOnGetDiscounts, fn: onGetDiscounts }];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    return (
        <div className={"CatalogItemDiscounts"}>
            <div>
                <Typeahead
                    isSelectableFn={isSelectable}
                    typeaheadApiFn={getDiscounts}
                    typeaheadItems={typeaheadOptions}
                    titleProp={"name"}
                    subTitleProp={"discount"}
                    selectedItemFn={(discount: IDiscount) => {
                        setSelectedDiscount(discount);
                    }}
                    confirmAdd={true}
                    clearTypeahead={clearTypeahead}
                    clearTypeaheadCallback={clearTypeaheadFn}
                    disabled={props.maxAllowed ? props.maxAllowed === props.selectedDiscounts.length : false}
                    placeholder={"search_discounts"}
                />
            </div>

            <div className="mt-2">
                <div className={"d-flex w-100"}>
                    <div className={"w-3r"}></div>
                    <div className={"w-5r"}>
                        <OwLabel title={"active"} isBold={true} />
                    </div>
                    <div className={"w-10r"}>
                        <OwLabel title={"name"} isBold={true} />
                    </div>
                    <div className={"w-5r"}>
                        <OwLabel title={"discount"} isBold={true} />
                    </div>
                </div>
                {discounts.map((discount) => {
                    return (
                        <div key={discount.guid} className={"d-flex w-100"}>
                            <div className={"w-3r"}>
                                <OwIconButton
                                    iconClass={"fas fa-trash-alt"}
                                    iconColorClass={"text-danger"}
                                    fn={() => {
                                        removeDiscount(discount);
                                    }}
                                />
                            </div>
                            <div className={"w-5r"}>
                                {discount.active ? (
                                    <i className={"fas fa-check text-success"} />
                                ) : (
                                    <i className={"fas fa-times text-danger"} />
                                )}
                            </div>
                            <div className={"w-10r"}>{discount.name}</div>
                            <div>
                                {discount.discount}
                                {discount.discount_type_id === PriceUpchargeTypeEnum.PERCENT ? "%" : ""}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
